import { HandicapMode } from "types/enums";
import { styles } from "./Scorecard.styles";
import { useState } from "react";
import type {
  MatchResults,
  MatchScorecardResults,
  PlayerScorecardDto,
  ScorecardHoleElementDto,
  TeamScorecardDto,
} from "ScorecardModels";
import {
  holeNumberMatchPlayHelper,
  holesWonHelper,
  runningResultWinnerHelper,
  scorecardHoleStylesHelper,
  teamColorHelper,
} from "../../modules/scorecard-lookup/scorecardHelpers";
import { first, hasKeys } from "@/utils/object";
import { titleCase } from "text-case";

type ScorecardHolesLayoutProps = {
  playerScorecard: PlayerScorecardDto[];
  teamScorecard?: TeamScorecardDto;
  scorecardId?: string;
  showNetScore?: boolean;
  matchResults?: MatchResults;
  scrambleLeague?: boolean;
  handicapMode?: HandicapMode;
};

type HoleNumberProps = {
  holes: ScorecardHoleElementDto[];
  scorecardId?: string;
  showNetScore?: boolean;
  backNine?: boolean;
  holesWon?: MatchScorecardResults[];
  teamId?: number;
};
interface HeaderProps {
  holes: ScorecardHoleElementDto[];
  par?: number;
  index?: number;
  holesWon?: MatchScorecardResults[];
  backNine?: boolean;
}
interface PlayerRowScoreProps {
  holes: ScorecardHoleElementDto[];
  par?: number;
  index?: number;
  scorecard: PlayerScorecardDto;
  showNetScore?: boolean;
  backNine?: boolean;
  scorecardId?: string;
  matchResults?: MatchResults;
  firstPlayerTeam?: number;
}

type ScrambleLeagueRowProps = {
  playerScorecard: PlayerScorecardDto[];
  showNetScore?: boolean;
  showBackNine?: boolean;
  teamId?: number;
  scorecardId?: string;
};

const HoleNumberRow: React.FC<HoleNumberProps> = (props) => {
  const { holes, holesWon } = props;

  const matchPlayHoleWinner = (holeNumber: number) => {
    const hole =
      holesWon?.filter((hole) => hole.holeNumber === holeNumber) || [];

    return hole[0]?.holeResult;
  };

  return (
    <>
      {holes.map((hole, index) => {
        return (
          <div
            className={styles.scorecardLayoutColumn}
            key={`holeNumber-${index}`}
          >
            {holesWon ? (
              <div
                className={`flex justify-center items-center  ${
                  holeNumberMatchPlayHelper[
                    matchPlayHoleWinner(hole.holeNumber)
                  ]
                }`}
              >
                <div className="flex justify-center text-center ">
                  {hole.holeNumber}
                </div>
              </div>
            ) : (
              <>{hole.holeNumber}</>
            )}
          </div>
        );
      })}
    </>
  );
};

const ParRow: React.FC<HoleNumberProps> = (props) => {
  const { holes } = props;

  return (
    <>
      {holes.map((hole, index) => {
        return (
          <div className={styles.scorecardLayoutParColumn} key={`par-${index}`}>
            {hole.par}
          </div>
        );
      })}
    </>
  );
};

const MatchPlayRow: React.FC<HoleNumberProps> = (props) => {
  const { holesWon, holes, backNine } = props;
  const matchCalledHole =
    holesWon?.find((hole) => hole.matchCalled === true)?.holeNumber || 18;

  return (
    <>
      <div className={styles.scorecardLayoutPlayerRow}>
        <div className={styles.scorecardLayoutMatchRow}>
          <div className="font-brandDarkGray font-normal pr-2 truncate text-left">
            Score
          </div>
        </div>
        {holesWon &&
          holes.map((hole, index) => {
            return (
              <div
                className={styles.scorecardLayoutParColumn}
                key={`par-${index}`}
              >
                {matchCalledHole >= hole.holeNumber && (
                  <span
                    className={
                      runningResultWinnerHelper[
                        holesWon[
                          backNine ? hole.holeNumber - 10 : hole.holeNumber - 1
                        ]?.runningMatchWinner
                      ]
                    }
                  >
                    {holesWon[
                      backNine ? hole.holeNumber - 10 : hole.holeNumber - 1
                    ]?.matchCalled
                      ? holesWonHelper(
                          holesWon[
                            backNine
                              ? hole.holeNumber - 10
                              : hole.holeNumber - 1
                          ],
                          holes,
                        )
                      : holesWon[
                          backNine ? hole.holeNumber - 10 : hole.holeNumber - 1
                        ]?.runningResult}
                  </span>
                )}
              </div>
            );
          })}
        <div className={styles.scorecardLayoutPlayerRowEnd}></div>
      </div>
    </>
  );
};

const ScoreRow: React.FC<HoleNumberProps> = (props) => {
  const { holes, showNetScore } = props;

  return (
    <>
      {holes.map((hole, index) => {
        return (
          <div
            className={`${styles.scorecardLayoutPlayerColumn}`}
            key={`playerScore-${index}`}
          >
            <div
              className={`${
                hole.selected &&
                scorecardHoleStylesHelper(
                  showNetScore ? hole.net?.final || 0 : hole.gross?.final || 0,
                  hole?.par,
                )
              } flex items-center relative h-6`}
            >
              <div className="flex flex-1 justify-center text-gray-700 text-center ">
                {hole?.conceded ? "X" : ""}
                {hole.selected
                  ? showNetScore
                    ? hole.net?.final || 0
                    : hole.gross?.final || 0
                  : "-"}
              </div>
              {hole.selected && showNetScore && hole.net?.strokes !== 0 && (
                <div className="strokesGiven">{hole.net?.strokes}</div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

const ScorecardHeader: React.FC<HeaderProps> = ({
  holes,
  par,
  holesWon,
  backNine,
}) => (
  <>
    <div className={styles.scorecardLayoutHeaderRow}>
      <div className={styles.scorecardLayoutColumnStart}>Hole</div>
      <HoleNumberRow holes={holes} holesWon={holesWon} />
      <div className={styles.scorecardLayoutColumnEnd}>
        {backNine ? "In" : "Out"}
      </div>
    </div>
    <div className={styles.scorecardLayoutParRow}>
      <div className={styles.scorecardLayoutColumnStart}>Par</div>
      <ParRow holes={holes} />
      <div className={styles.scorecardLayoutColumnEnd}>{par}</div>
    </div>
  </>
);

function filterHolesInRange<T extends { holeNumber: number }>(
  holes: T[] | undefined,
  start: number,
  end: number,
): T[] {
  if (!holes) return [];
  return holes.filter((h) => h.holeNumber >= start && h.holeNumber <= end);
}

function getFrontNineHoles<T extends { holeNumber: number }>(
  holes: T[] | undefined,
): T[] {
  return filterHolesInRange(holes, 1, 9);
}

function getBackNineHoles<T extends { holeNumber: number }>(
  holes: T[] | undefined,
): T[] {
  return filterHolesInRange(holes, 10, 18);
}

function countSelectedHoles(
  holes: ScorecardHoleElementDto[] | undefined,
): number {
  if (!holes) return 0;
  return holes.filter((h) => h.selected !== false).length;
}

type FrontAndBackNineToggleProps = {
  defaultFrontNine: ScorecardHoleElementDto[];
  defaultBackNine: ScorecardHoleElementDto[];
  showFrontNine: boolean;
  onSetFrontNine: (value: boolean) => void;
};

const FrontAndBackNineToggle: React.FC<FrontAndBackNineToggleProps> = ({
  defaultFrontNine,
  defaultBackNine,
  showFrontNine,
  onSetFrontNine,
}) => {
  return (
    <>
      {countSelectedHoles(defaultFrontNine) > 0 &&
        countSelectedHoles(defaultBackNine) > 0 && (
          <div className={styles.frontAndBackToggleContainer}>
            <button
              className={`${
                showFrontNine ? styles.frontAndBackToggleButtonActive : ""
              } ${styles.frontAndBackToggleButtonInactive}`}
              id="grid"
              onClick={() => onSetFrontNine(true)}
            >
              Front 9
            </button>
            <button
              className={` ${
                !showFrontNine ? styles.frontAndBackToggleButtonActive : ""
              } ${styles.frontAndBackToggleButtonInactive}`}
              id="list"
              onClick={() => onSetFrontNine(false)}
            >
              Back 9
            </button>
          </div>
        )}
    </>
  );
};

const ScrambleLeagueRow: React.FC<ScrambleLeagueRowProps> = (props) => {
  const { playerScorecard, showNetScore, showBackNine, scorecardId } = props;

  return (
    <>
      {playerScorecard
        .filter((scorecard) => {
          return scorecard.team === 1;
        })
        .map((scorecard, index) => {
          return (
            <div
              key={scorecard.playerGuid || index}
              className={styles.scorecardLayoutPlayerRow}
            >
              <div className={styles.scorecardLayoutPlayerName}>
                <div className="font-brandDarkGray font-normal pr-2 truncate">
                  {scorecard.playerName}
                </div>
              </div>
              {index === 0 && (
                <ScoreRow
                  holes={
                    showBackNine
                      ? getBackNineHoles(scorecard.holes)
                      : getFrontNineHoles(scorecard.holes)
                  }
                  scorecardId={scorecardId}
                  showNetScore={showNetScore}
                />
              )}
              {index === 0 && (
                <div className={styles.scorecardLayoutPlayerRowEnd}>
                  {showBackNine
                    ? showNetScore
                      ? scorecard?.net.in
                      : scorecard?.gross.in
                    : showNetScore
                      ? scorecard?.net.out
                      : scorecard?.gross.out}
                </div>
              )}
            </div>
          );
        })}
      {playerScorecard
        .filter((scorecard) => {
          return scorecard.team === 2;
        })
        .map((scorecard, index) => {
          return (
            <div
              key={scorecard.playerGuid || index}
              className={styles.scorecardLayoutPlayerRow}
            >
              <div className={styles.scorecardLayoutPlayerName}>
                <div className="font-brandDarkGray font-normal pr-2 truncate">
                  {scorecard.playerName}
                </div>
              </div>
              {index === 0 && (
                <ScoreRow
                  holes={
                    showBackNine
                      ? getBackNineHoles(scorecard.holes)
                      : getFrontNineHoles(scorecard.holes)
                  }
                  scorecardId={scorecardId}
                  showNetScore={showNetScore}
                />
              )}
              {index === 0 && (
                <div className={styles.scorecardLayoutPlayerRowEnd}>
                  {showBackNine
                    ? showNetScore
                      ? scorecard?.net.in
                      : scorecard?.gross.in
                    : showNetScore
                      ? scorecard?.net.out
                      : scorecard?.gross.out}
                </div>
              )}
            </div>
          );
        })}
    </>
  );
};

const PlayerScoreRow: React.FC<PlayerRowScoreProps> = (props) => {
  const {
    holes,
    index,
    scorecard,
    showNetScore,
    backNine,
    scorecardId,
    matchResults,
    firstPlayerTeam,
  } = props;

  return (
    <>
      <div className={styles.scorecardLayoutPlayerRow}>
        <div className={styles.scorecardLayoutPlayerName}>
          <div className="font-brandDarkGray font-normal pr-2 truncate text-left">
            {matchResults && index && (
              <div
                className={`inline-block mt-1 mr-1 h-2 w-2 ${teamColorHelper[index]}`}
              ></div>
            )}
            {scorecard.playerName}
            <br />
          </div>
        </div>
        <ScoreRow
          holes={holes}
          scorecardId={scorecardId}
          showNetScore={showNetScore}
          teamId={firstPlayerTeam}
          backNine={backNine}
        />
        <div className={styles.scorecardLayoutPlayerRowEnd}>
          {backNine
            ? showNetScore
              ? scorecard?.net?.in
              : scorecard?.gross?.in
            : showNetScore
              ? scorecard?.net?.out
              : scorecard?.gross?.out}
        </div>
      </div>
      {matchResults && index === 0 && (
        <MatchPlayRow
          holes={
            backNine
              ? scorecard?.holes?.filter((hole) => hole.holeNumber < 9)
              : getFrontNineHoles(scorecard?.holes)
          }
          holesWon={
            backNine
              ? matchResults?.holesWon?.filter((hole) => hole.holeNumber < 9)
              : getFrontNineHoles(matchResults?.holesWon)
          }
          backNine={false}
        />
      )}
    </>
  );
};

const ScorecardHolesLayout: React.FC<ScorecardHolesLayoutProps> = (props) => {
  const {
    playerScorecard,
    scorecardId,
    teamScorecard,
    showNetScore,
    matchResults,
    scrambleLeague,
    handicapMode,
  } = props;

  const scorecard = playerScorecard[0];

  const [showFrontNine, setFrontNine] = useState<boolean>(true);

  const defaultFrontNine = getFrontNineHoles(scorecard?.holes);
  const defaultBackNine = getBackNineHoles(scorecard?.holes);

  const parFrontNine = defaultFrontNine
    ?.map((hole: ScorecardHoleElementDto) => hole.par)
    .reduce((a: number, b: number) => a + b, 0);
  const parBackNine = defaultBackNine
    ?.map((hole: ScorecardHoleElementDto) => hole.par)
    .reduce((a: number, b: number) => a + b, 0);

  const showFrontAndBackNine =
    countSelectedHoles(defaultFrontNine) > 0 &&
    countSelectedHoles(defaultBackNine) > 0;

  return (
    <div
      key={`${first(playerScorecard)?.playerGuid}-${first(playerScorecard)?.playerName}-${scorecardId}`}
      id="scorecardLayoutContainerX"
    >
      <div className="flex flex-row m-0 p-0 justify-end">
        <div className="flex justify-start w-3/5">
          <FrontAndBackNineToggle
            defaultFrontNine={defaultFrontNine}
            defaultBackNine={defaultBackNine}
            showFrontNine={showFrontNine}
            onSetFrontNine={setFrontNine}
          />
          {handicapMode !== undefined && (
            <div className="poppins ml-2 flex items-center text-xs text-gray-500 dark:text-gray-200 ">
              Handicap: {titleCase(HandicapMode[handicapMode])}
            </div>
          )}
        </div>
        <div className="tooltip-container text-xs text-gray-500 dark:text-gray-50 flex items-center justify-end w-2/5 text-brandDarkGreen"></div>
      </div>
      {matchResults ? (
        <>
          {((showFrontAndBackNine && showFrontNine) ||
            (!showFrontAndBackNine &&
              countSelectedHoles(defaultFrontNine) > 0)) && (
            <div
              className={styles.scorecardLayoutContainer}
              key={`${scorecard?.playerGuid}-frontNine-match`}
            >
              <ScorecardHeader
                holes={defaultFrontNine || ([] as ScorecardHoleElementDto[])}
                par={parFrontNine}
                holesWon={matchResults.holesWon}
                backNine={false}
              />

              {playerScorecard
                ?.filter((player) => player.team === 1)
                ?.map((scorecard) => {
                  return (
                    <PlayerScoreRow
                      key={scorecard.playerGuid}
                      holes={getFrontNineHoles(scorecard?.holes)}
                      index={scorecard.team}
                      scorecard={scorecard}
                      showNetScore={showNetScore}
                      backNine={false}
                      scorecardId={scorecardId}
                      matchResults={matchResults}
                      firstPlayerTeam={first(playerScorecard)?.team}
                    />
                  );
                })}
              <MatchPlayRow
                holes={getFrontNineHoles(scorecard?.holes)}
                holesWon={getFrontNineHoles(matchResults.holesWon)}
                backNine={false}
              />
              {playerScorecard
                ?.filter((player) => player.team === 2)
                ?.map((scorecard) => {
                  return (
                    <PlayerScoreRow
                      key={scorecard.playerGuid}
                      holes={getFrontNineHoles(scorecard.holes)}
                      index={scorecard.team}
                      scorecard={scorecard}
                      showNetScore={showNetScore}
                      backNine={false}
                      scorecardId={scorecardId}
                      matchResults={matchResults}
                      firstPlayerTeam={first(playerScorecard)?.team}
                    />
                  );
                })}
            </div>
          )}
          {((showFrontAndBackNine && !showFrontNine) ||
            (!showFrontAndBackNine &&
              countSelectedHoles(defaultBackNine) > 0)) && (
            <div
              className={styles.scorecardLayoutContainer}
              key={`${scorecard?.playerGuid}-backNine-match`}
            >
              <ScorecardHeader
                holes={defaultBackNine || ([] as ScorecardHoleElementDto[])}
                par={parBackNine}
                holesWon={matchResults.holesWon}
                backNine={true}
              />

              {playerScorecard
                ?.filter((player) => player.team === 1)
                ?.map((scorecard) => {
                  return (
                    <PlayerScoreRow
                      key={scorecard.playerGuid}
                      holes={getBackNineHoles(scorecard.holes)}
                      index={scorecard.team}
                      scorecard={scorecard}
                      showNetScore={showNetScore}
                      backNine={true}
                      scorecardId={scorecardId}
                      matchResults={matchResults}
                      firstPlayerTeam={first(playerScorecard)?.team}
                    />
                  );
                })}

              <MatchPlayRow
                holes={getBackNineHoles(scorecard?.holes)}
                holesWon={getBackNineHoles(matchResults.holesWon)}
                backNine={true}
              />

              {playerScorecard
                ?.filter((player) => player.team === 2)
                ?.map((scorecard) => {
                  return (
                    <PlayerScoreRow
                      key={scorecard.playerGuid}
                      holes={getBackNineHoles(scorecard.holes)}
                      index={scorecard.team}
                      scorecard={scorecard}
                      showNetScore={showNetScore}
                      backNine={true}
                      scorecardId={scorecardId}
                      matchResults={matchResults}
                      firstPlayerTeam={first(playerScorecard)?.team}
                    />
                  );
                })}
            </div>
          )}
        </>
      ) : (
        <>
          {((showFrontAndBackNine && showFrontNine) ||
            (!showFrontAndBackNine &&
              countSelectedHoles(defaultFrontNine) > 0)) && (
            <div
              className={styles.scorecardLayoutContainer}
              key={`${scorecard?.playerGuid}-frontNine`}
            >
              <ScorecardHeader
                holes={defaultFrontNine || ([] as ScorecardHoleElementDto[])}
                par={parFrontNine}
              />

              {scrambleLeague ? (
                <>
                  <ScrambleLeagueRow
                    showBackNine={false}
                    playerScorecard={playerScorecard}
                    showNetScore={showNetScore}
                    scorecardId={scorecardId}
                  />
                </>
              ) : (
                playerScorecard.map((scorecard, index) => {
                  return (
                    <PlayerScoreRow
                      key={scorecard.playerGuid || index}
                      holes={getFrontNineHoles(scorecard.holes)}
                      index={index}
                      scorecard={scorecard}
                      showNetScore={showNetScore}
                      scorecardId={scorecardId}
                      matchResults={matchResults}
                      firstPlayerTeam={first(playerScorecard)?.team}
                    />
                  );
                })
              )}

              {teamScorecard && hasKeys(teamScorecard) && (
                <div className={styles.scorecardLayoutPlayerRow}>
                  <div className={styles.scorecardLayoutPlayerName}>
                    <div className="font-brandDarkGray font-normal pr-2 truncate">
                      {teamScorecard.teamName}
                    </div>
                  </div>
                  <ScoreRow
                    holes={getFrontNineHoles(teamScorecard?.holes)}
                    scorecardId={scorecardId}
                    showNetScore={showNetScore}
                  />
                  <div className={styles.scorecardLayoutPlayerRowEnd}>
                    {showNetScore
                      ? teamScorecard.net.out
                      : teamScorecard.gross.out}
                  </div>
                </div>
              )}
            </div>
          )}
          {((showFrontAndBackNine && !showFrontNine) ||
            (!showFrontAndBackNine &&
              countSelectedHoles(defaultBackNine) > 0)) && (
            <div
              className={styles.scorecardLayoutContainer}
              key={`${scorecard?.playerGuid}-backNine`}
            >
              <ScorecardHeader
                holes={defaultBackNine || ([] as ScorecardHoleElementDto[])}
                par={parBackNine}
                backNine={true}
              />

              {scrambleLeague ? (
                <>
                  <ScrambleLeagueRow
                    showBackNine={true}
                    playerScorecard={playerScorecard}
                    showNetScore={showNetScore}
                    scorecardId={scorecardId}
                  />
                </>
              ) : (
                playerScorecard.map((scorecard, index) => {
                  return (
                    <PlayerScoreRow
                      key={scorecard.playerGuid || index}
                      holes={getBackNineHoles(scorecard.holes)}
                      index={index}
                      scorecard={scorecard}
                      showNetScore={showNetScore}
                      backNine={true}
                      scorecardId={scorecardId}
                      matchResults={matchResults}
                      firstPlayerTeam={first(playerScorecard)?.team}
                    />
                  );
                })
              )}

              {teamScorecard && hasKeys(teamScorecard) && (
                <div className={styles.scorecardLayoutPlayerRow}>
                  <div className={styles.scorecardLayoutPlayerName}>
                    <div className="font-brandDarkGray font-normal pr-2 truncate">
                      {teamScorecard.teamName}
                    </div>
                  </div>
                  <ScoreRow
                    holes={getBackNineHoles(teamScorecard?.holes)}
                    scorecardId={scorecardId}
                    showNetScore={showNetScore}
                    backNine={true}
                  />
                  <div className={styles.scorecardLayoutPlayerRowEnd}>
                    {showNetScore
                      ? teamScorecard.net.in
                      : teamScorecard.gross.in}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
export default ScorecardHolesLayout;
